import { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Sparkles, Check, X } from 'lucide-react';

export default function DailySuggestions() {
  const { token } = useAuthStore();
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadSuggestions();
    }
  }, [token]);

  const loadSuggestions = async () => {
    try {
      const data = await api.getDailySuggestions();
      setSuggestions(data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSuggestions = async () => {
    setGenerating(true);
    try {
      await api.generateDailySuggestions();
      await loadSuggestions();
    } catch (error) {
      console.error('Failed to generate suggestions:', error);
    } finally {
      setGenerating(false);
    }
  };

  const approveSuggestion = async (id: number) => {
    try {
      await api.approveSuggestion(id);
      await loadSuggestions();
    } catch (error) {
      console.error('Failed to approve suggestion:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Daily Suggestions</h1>
            <p className="text-gray-600 mt-2">AI-generated post ideas for today</p>
          </div>
          <button
            onClick={generateSuggestions}
            disabled={generating}
            className="btn btn-primary flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? 'Generating...' : 'Generate New Suggestions'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="card text-center py-12">
            <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No suggestions yet</h3>
            <p className="text-gray-600 mb-6">Generate your first AI-powered post suggestions</p>
            <button onClick={generateSuggestions} className="btn btn-primary">
              Generate Suggestions
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {suggestions.map((suggestion) => (
              <div key={suggestion.id} className="card">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-sm font-medium text-primary-600 uppercase">
                      {suggestion.post_type}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900 mt-1">Post Idea</h3>
                  </div>
                  <span
                    className={`text-xs px-3 py-1 rounded-full ${
                      suggestion.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {suggestion.status}
                  </span>
                </div>

                <p className="text-gray-700 mb-4">{suggestion.caption}</p>

                {suggestion.hashtags && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {JSON.parse(suggestion.hashtags).map((tag: string, i: number) => (
                      <span key={i} className="text-sm text-primary-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {suggestion.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveSuggestion(suggestion.id)}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Approve & Schedule
                    </button>
                    <button className="btn btn-secondary flex items-center gap-2">
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
